import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  Grid,
  Autocomplete,
  Typography,
  CardMedia,
  Box,
  Card,
  FormLabel,
  useTheme,
  Paper,
  Fade,
  Chip,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./quillCustomStyles.css";
import {
  recipeCategories,
  recipeTypes,
  servings,
} from "../../Static/recipe-library";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CategoryIcon from "@mui/icons-material/Category";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import { getRecipeImage } from "../../constants";

const RecipeDetailsForm = ({
  handleCoverFileChange,
  coverFile,
  defaultValues,
}) => {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          p: { xs: 2, md: 4 },
          background: "transparent",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: theme.palette.primary.main,
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            pl: 2,
          }}
        >
          Create Your Recipe
        </Typography>

        <Grid container spacing={4}>
          {/* Recipe Name */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                <RestaurantIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                Recipe Details
              </Typography>
              <Controller
                name="recipe_name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Recipe Name"
                    variant="outlined"
                    fullWidth
                    required
                    error={!!errors.recipe_name}
                    helperText={
                      errors.recipe_name ? errors.recipe_name.message : ""
                    }
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: theme.palette.divider,
                          borderWidth: 2,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.light,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      "& label": {
                        color: theme.palette.text.secondary,
                      },
                      "& label.Mui-focused": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                )}
              />

              {/* Recipe Type */}
              <Controller
                name="recipe_type"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={recipeTypes}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Recipe Type"
                        variant="outlined"
                        fullWidth
                        error={!!errors.recipe_type}
                        helperText={
                          errors.recipe_type ? errors.recipe_type.message : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: theme.palette.divider,
                              borderWidth: 2,
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.light,
                            },
                          },
                        }}
                      />
                    )}
                    onChange={(event, newValue) => field.onChange(newValue)}
                  />
                )}
              />
            </Paper>
          </Grid>

          {/* Recipe Image */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <FormLabel
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: "1.25rem",
                  color: theme.palette.text.primary,
                }}
              >
                <CategoryIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                Recipe Image
              </FormLabel>
              <Card
                variant="outlined"
                sx={{
                  border: "2px dashed",
                  borderColor: coverFile
                    ? theme.palette.success.main
                    : theme.palette.divider,
                  borderRadius: 3,
                  height: 260,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: 6,
                  },
                }}
              >
                {coverFile ? (
                  <Box sx={{ position: "relative", height: "100%" }}>
                    <CardMedia
                      component="img"
                      image={
                        coverFile instanceof Blob
                          ? URL.createObjectURL(coverFile)
                          : coverFile ||
                            getRecipeImage(defaultValues?.recipe_image)
                      }
                      alt={defaultValues?.recipe_name || "Preview"}
                      sx={{
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.9)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        background:
                          "linear-gradient(transparent, rgba(0,0,0,0.6))",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#fff", fontWeight: 500 }}
                      >
                        {coverFile.name}
                      </Typography>
                      <Chip
                        label="Change"
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.text.secondary,
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 60,
                        mb: 2,
                        color: theme.palette.primary.light,
                        animation: "pulse 2s infinite ease-in-out",
                        "@keyframes pulse": {
                          "0%": { opacity: 0.7 },
                          "50%": { opacity: 1 },
                          "100%": { opacity: 0.7 },
                        },
                      }}
                    />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Drop your image here
                    </Typography>
                    <Typography variant="body2">
                      Click to upload or drag and drop
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: theme.palette.text.secondary }}
                    >
                      PNG, JPG (max 10MB)
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="recipe-image-upload"
                  onChange={handleCoverFileChange}
                />
                <label
                  htmlFor="recipe-image-upload"
                  aria-label="Upload recipe image"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                />
              </Card>
            </Paper>
          </Grid>

          {/* Recipe Categories & Serving */}
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  color: theme.palette.text.primary,
                }}
              >
                <PeopleIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Recipe Category & Serving
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="serving"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        options={servings}
                        getOptionLabel={(option) =>
                          `${option} Person${option > 1 ? "s" : ""}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Serving"
                            variant="outlined"
                            fullWidth
                            error={!!errors.serving}
                            helperText={
                              errors.serving ? errors.serving.message : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              sx: { borderRadius: 2 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: theme.palette.divider,
                                  borderWidth: 2,
                                },
                                "&:hover fieldset": {
                                  borderColor: theme.palette.primary.light,
                                },
                              },
                            }}
                          />
                        )}
                        onChange={(event, newValue) => field.onChange(newValue)}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="recipe_category"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        multiple
                        options={recipeCategories}
                        freeSolo
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={`${index}-${option}`}
                              label={option}
                              {...getTagProps({ index })}
                              variant="filled"
                              color="primary"
                              size="medium"
                              sx={{
                                m: 0.5,
                                borderRadius: "16px",
                                fontWeight: 500,
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Recipe Category"
                            variant="outlined"
                            fullWidth
                            error={!!errors.recipe_category}
                            helperText={
                              errors.recipe_category
                                ? errors.recipe_category.message
                                : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              sx: { borderRadius: 2 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: theme.palette.divider,
                                  borderWidth: 2,
                                },
                                "&:hover fieldset": {
                                  borderColor: theme.palette.primary.light,
                                },
                              },
                            }}
                          />
                        )}
                        onChange={(event, newValue) => field.onChange(newValue)}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Recipe Description */}
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  color: theme.palette.text.primary,
                }}
              >
                <DescriptionIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                Recipe Description
              </Typography>

              <Controller
                name="recipe_description"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <>
                    <Box
                      sx={{
                        border: `2px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                        "&:hover": {
                          borderColor: theme.palette.primary.light,
                        },
                      }}
                    >
                      <ReactQuill
                        {...field}
                        theme="snow"
                        placeholder="Share the story behind this recipe, special preparation tips, or serving suggestions..."
                        onChange={field.onChange}
                        value={field.value || ""}
                        className="quill-editor-enhanced"
                        style={{ height: "350px", marginBottom: "40px" }}
                      />
                    </Box>
                    {errors.recipe_description && (
                      <Typography
                        sx={{ color: theme.palette.error.main, mt: 1 }}
                      >
                        {errors.recipe_description.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Fade>
  );
};

export default RecipeDetailsForm;
